/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react';
import cn from 'classnames';
import {LogSource} from 'commerceAPI/custom-apis/CustomShopperSystem/ICustomShopperSystem';

import {useLocation, useRequestClass} from 'coreHooks';

import {useGeneralProvider} from 'brand/app/components/_app-config/GeneralProvider';
import Icon from 'brandComponents/Icon';
import SvgSprite from 'brandComponents/SvgSprite';

// <Error> is rendered when:
//
// 1. A user requests a page that is not routable from `app/routes.jsx`.
// 2. A routed component throws an error in `getProps()`.
// 3. A routed component throws an error in `render()`.
//
// It must not throw an error. Keep it as simple as possible.
import styles from './Error.module.scss';

const Error = (props) => {
	const {managerRepository} = useGeneralProvider();
	const location = useLocation();
	const {message, stack} = props;
	const logProvider = managerRepository.getLogMgr().getLogProvider();
	const {isProduction} = useRequestClass();

	if (logProvider) {
		logProvider.error(`${message} at "${location.pathname}"`, LogSource.PAGE, stack);
	}

	return (
		<div className={cn(styles['error-page'])}>
			<SvgSprite />
			<header>
				<div className={cn(styles['error-page-logo'])}>
					<a href="/">
						<Icon
							name="casadei-logo"
							classNames="header__logo-img"
						/>
					</a>
				</div>
			</header>
			<main>
				<div className={cn(styles['error-page-content'])}>
					<div className={cn(styles['error-page-wrapper'])}>
						<p>We are sorry you are unable to access our site, we are working to optimise our service.</p>
						<p>Please check back in a few hours.</p>
					</div>
					<p>If you have a customer services enquiry, please email enquiries@casadei.com.</p>
				</div>
			</main>

			{!isProduction ?
				<div className={cn(styles['error-page-errors'])}>
					<h2>Please take a screenshot to this page and attach it to a ticket.</h2>

					{message ? <pre>{message}</pre> : null}
					{stack ? <pre>{stack}</pre> : null}
				</div>
				:
				null
			}
		</div>
	);
};

export default Error;
